import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

import { anularEnvio, anularUso, envios } from "../services/panol";
import { actual as quienSoy } from "../services/quien";
import {
  ETIQUETAS,
  demorado,
  estado,
  etiqueta,
  masUsados,
  quedan,
  recepcionDeducida,
  resumen,
} from "../shared/panol";
import { desdeClave, ruta as rutaDeUnidad } from "../shared/unidad";
import BotonConfirmar from "../components/funcionales/BotonConfirmar";
import ModoEdicion from "../components/funcionales/ModoEdicion";
import Loading from "./Loading";

/* Los movimientos del pañol: qué salió de Buenos Aires, a qué sede llegó y en
 * qué se usó.
 *
 * ── VIVÍA EN PUERTO Y AHORA ES DEL PAÑOL (Igna, 11/09) ────────────────────
 *
 * Nació cuando el pañol era «el del puerto». Ahora el pañol tiene las siete
 * sedes, cada una con su página, y ésta es la ÚNICA que las cruza — y la única
 * donde se anula. Por eso se movió de sector en vez de borrarse: las páginas
 * por sede son de mirar, así que sacarla del menú se llevaba puesta la única
 * forma de deshacer un dedazo.
 *
 * ── LO QUE ESTA PANTALLA CONTESTA ─────────────────────────────────────────
 *
 * Nico: *«cuando salen del pañol de BsAs quede a dónde van, y se dan de alta
 * en el puerto, y cuando se usan quede registrado»*. Son tres hechos con fecha
 * sobre el mismo objeto, y el que falta es el que importa: **despachado y
 * nunca recibido** es el repuesto que se perdió en el camino.
 *
 * ── LOS SIN RECIBIR VAN ARRIBA Y SEPARADOS ────────────────────────────────
 *
 * No es un orden: es un bloque propio. Mezclados con los cincuenta que ya
 * llegaron, los tres que faltan hay que ir a buscarlos — y una lista de
 * control que hay que filtrar para usar no se usa. Es lo mismo que hace la
 * home con lo que vence.
 *
 * ── NO SE CARGA DESDE ACÁ ─────────────────────────────────────────────────
 *
 * Entra por WhatsApp: `pañol mandé 2 filtro de aire`. Lo único que se escribe
 * es **anular**, que es lo único que saca un envío del control y por eso es lo
 * único que no se hace desde el teléfono.
 */

const fecha = (iso) => {
  if (!iso) return "";
  /* UTC−3 fijo y no la hora del navegador: el mismo envío tiene que decir la
   * misma fecha acá y en el WhatsApp del que lo despachó. Es la regla de
   * `shared/puerto.js`. */
  const d = new Date(new Date(iso).getTime() - 3 * 3600 * 1000);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}`;
};

const Interno = ({ interno }) => {
  if (!interno) {
    /* SIN UNIDAD NO ES UN ERROR y se dibuja como lo que es. Se puede anotar un
     * uso sin decir en qué camión —frenar por eso haría que no se anote nada—
     * y el hueco a la vista es lo que hace que alguien lo complete. */
    return <span className="pn-sin">sin unidad</span>;
  }
  const u = desdeClave(interno);
  const r = u && rutaDeUnidad(u.tipo, u.interno);
  return r ? <Link to={r}>{interno}</Link> : <span>{interno}</span>;
};

const Fila = ({ e, tocando, yendo, onAnular, onAnularUso }) => {
  const est = estado(e);
  const usos = (e.usos || []).filter((u) => !u.anulado_en);
  return (
    <tr className={demorado(e) ? "pn-demorado" : ""}>
      <td>
        <b>{etiqueta(e.id)}</b>
        <span className="sub">{fecha(e.despachado_en)}</span>
      </td>
      <td>
        {e.que}
        <span className="sub">
          {Number(e.cantidad)} despachada{Number(e.cantidad) === 1 ? "" : "s"}
          {est !== "en_camino" && ` · quedan ${quedan(e)}`}
        </span>
      </td>
      <td>
        <span className={`pill pn-${est}`}>{ETIQUETAS[est]}</span>
        {demorado(e) && <span className="pn-aviso">hace más de una semana</span>}
        {recepcionDeducida(e) && (
          /* «Recibido» y «recibido PORQUE se usó» no son lo mismo: la segunda
           * es una recepción deducida y nadie la confirmó al llegar. */
          <span className="sub">se dedujo del uso</span>
        )}
      </td>
      <td>
        {usos.length === 0 ? (
          <span className="pn-sin">—</span>
        ) : (
          <ul className="pn-usos">
            {usos.map((u) => (
              <li key={u.id}>
                {Number(u.cantidad)} en <Interno interno={u.interno} />
                <span className="sub">{fecha(u.usado_en)}</span>
                {tocando && (
                  <BotonConfirmar
                    className="link-button"
                    disabled={yendo === `u${u.id}`}
                    pregunta="¿Anular el uso?"
                    si="Sí, anular"
                    onConfirmar={() => onAnularUso(u.id)}
                  >
                    anular uso
                  </BotonConfirmar>
                )}
              </li>
            ))}
          </ul>
        )}
      </td>
      <td>{e.despachado_nombre || e.recibido_nombre || ""}</td>
      {/* EN SU PROPIA COLUMNA, como en Trabajos y Novedades: pegado al nombre
          de quien lo despachó se leía como parte del nombre. Y PREGUNTA ANTES:
          anular saca el envío del control y desde acá no se vuelve a traer. */}
      {tocando && (
        <td>
          <BotonConfirmar
            className="link-button"
            disabled={yendo === `e${e.id}`}
            pregunta="¿Anular el envío?"
            si="Sí, anular"
            onConfirmar={() => onAnular(e.id)}
          >
            anular envío
          </BotonConfirmar>
        </td>
      )}
    </tr>
  );
};

const PuertoPanol = () => {
  const { permisos = null } = useOutletContext() || {};
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState(null);
  const [editando, setEditando] = useState(false);
  const [yendo, setYendo] = useState(null);

  /* Mientras los permisos no llegaron se asume que puede: esconder el lápiz
   * medio segundo y después mostrarlo se lee como que la pantalla titila. */
  /* Las dos llaves, y con cualquiera alcanza — igual que el ítem del menú. El
   * pañol dejó de ser del puerto (Igna, 11/09) y el sector es de pañol, pero la
   * gente que lo venía anulando tiene `cargar.puerto`. Escrito distinto del
   * menú, el lápiz aparecería para quien la pantalla no deja tocar nada. */
  const puedeEditar =
    !permisos ||
    permisos.includes("cargar.panol") ||
    permisos.includes("cargar.puerto");
  const tocando = puedeEditar && editando;

  const traer = useCallback(async () => {
    try {
      setFilas(await envios());
    } catch (e) {
      setError(e.message || "No se pudo leer el pañol");
    }
  }, []);

  useEffect(() => {
    traer();
  }, [traer]);

  const anular = async (id) => {
    setYendo(`e${id}`);
    try {
      await anularEnvio(id, quienSoy()?.mail || null);
      setFilas((prev) => (prev || []).filter((f) => f.id !== id));
    } catch (e) {
      setError(e.message || "No se pudo anular");
    } finally {
      setYendo(null);
    }
  };

  const quitarUso = async (id) => {
    setYendo(`u${id}`);
    try {
      await anularUso(id, quienSoy()?.mail || null);
      /* Se marca en memoria en vez de volver a pedir la lista: recargarla
       * mueve todo de lugar justo después de tocar una fila. */
      setFilas((prev) =>
        (prev || []).map((f) => ({
          ...f,
          usos: (f.usos || []).map((u) =>
            u.id === id ? { ...u, anulado_en: new Date().toISOString() } : u,
          ),
        })),
      );
    } catch (e) {
      setError(e.message || "No se pudo anular el uso");
    } finally {
      setYendo(null);
    }
  };

  if (error && !filas) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }
  if (!filas) return <Loading />;

  const r = resumen(filas);
  const sinRecibir = filas.filter((f) => estado(f) === "en_camino");
  const resto = filas.filter((f) => estado(f) !== "en_camino");
  const top = masUsados(filas, 8);

  const tabla = (lista) => (
    <div className="tabla-wrap">
      <table className="tabla fija">
        <thead>
          <tr>
            <th>Envío</th>
            <th>Qué</th>
            <th>Estado</th>
            <th>Dónde se usó</th>
            <th>Quién</th>
            {tocando && <th></th>}
          </tr>
        </thead>
        <tbody>
          {lista.map((e) => (
            <Fila
              key={e.id}
              e={e}
              tocando={tocando}
              yendo={yendo}
              onAnular={anular}
              onAnularUso={quitarUso}
            />
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="docs">
      <div className="tk-filtros">
        <ModoEdicion
          activo={editando}
          onCambiar={setEditando}
          puede={puedeEditar}
          que="el pañol"
        />
      </div>

      {/* `suave` y no `.panel-aviso` a secas: sin el modificador esto es un
          overlay absoluto que tapa el rail y la cabecera. */}
      {error && <p className="panel-aviso suave">{error}</p>}

      {filas.length === 0 && (
        <p className="panel-aviso suave">
          Todavía no salió nada del pañol.
        </p>
      )}

      {filas.length > 0 && (
        <>
          {/* SIN RECIBIR ARRIBA Y EN SU PROPIO BLOQUE. Mezclados con los que
              ya llegaron hay que ir a buscarlos, y una lista de control que
              hay que filtrar para usar no se usa. */}
          {sinRecibir.length > 0 && (
            <>
              <h3 className="pn-titulo">
                Sin recibir · {sinRecibir.length}
                {r.demorados > 0 && (
                  <span className="pn-aviso">
                    {r.demorados} hace más de una semana
                  </span>
                )}
              </h3>
              {tabla(sinRecibir)}
            </>
          )}

          <h3 className="pn-titulo">
            Ya llegaron · {resto.length}
            <span className="sub">
              {r.usados} usado{r.usados === 1 ? "" : "s"}
            </span>
          </h3>
          {resto.length > 0 ? (
            tabla(resto)
          ) : (
            <p className="panel-aviso suave">
              Todo lo que salió está todavía en camino.
            </p>
          )}

          {top.length > 1 && (
            <p className="docs-pie">
              <b>Lo que más se manda:</b>{" "}
              {top.map((x, i) => (
                <span key={x.que}>
                  {i > 0 && " · "}
                  {x.que} <b>({x.veces})</b>
                </span>
              ))}
            </p>
          )}
        </>
      )}

      <p className="docs-pie">
        Se carga por WhatsApp:{" "}
        <code>pañol mandé 2 filtro de aire</code> ·{" "}
        <code>pañol E-45 llegó</code> ·{" "}
        <code>pañol usé 1 de E-45 en el 010</code>.
      </p>
    </div>
  );
};

export default PuertoPanol;
